import React, { useState, useEffect, useRef } from "react";
import ReactPlayer from "react-player";
import styled from "styled-components";

// components
import WhisperSTTComponent from "./WhisperSTTComponent";
import TextToSpeech from "./TextToSpeech";

// Effects
import { Waves } from "@components/EngageApp/ChatVoice";

// Import Webm and Mov files
import ListeningWebm from "@video/Listen.webm";
import GreetingWebm from "@video/Greet.webm";
import InstructingWebm from "@video/Instruct.webm";
import ExplainingWebm from "@video/Explain.webm";
import AcknowledgingWebm from "@video/Acknowledge.webm";

import ListeningMov from "@video/Listen.mov";
import GreetingMov from "@video/Greet.mov";
import InstructingMov from "@video/Instruct.mov";
import ExplainingMov from "@video/Explain.mov";
import AcknowledgingMov from "@video/Acknowledge.mov";

// Image
import imgBackground from "@images/chatvoice/bg-engage.svg";
import imgBackgroundMobile from "@images/chatvoice/bg-engage-mobile.svg";

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const FrameChatFullBox = styled.div`
  position: relative;
  height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  background-image: url(${imgBackground});
  background-repeat: repeat-x;
  background-position: center;
  background-size: cover;
  @media (max-width: 768px) {
    background-image: url(${imgBackgroundMobile});
  }
`;

const ChatWrapper = styled.div`
  display: flex;
  flex: 1;
  gap: 20px;
  overflow: hidden;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const VideoWrapper = styled.div`
  flex: 0 0 40%;
  height: 100%;
  position: relative;
  overflow: hidden;
  pointer-events: none;

  video::-webkit-media-controls {
    display: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-height: 400px;
    z-index: -1;
    video {
      transform: scale(1.5);
      outline: none;
      &:focus,
      &:active {
        outline: none;
      }
    }
  }
`;

// Chuyển từ opacity sang visibility để tránh lag khi ẩn video không active
const VideoLayer = styled.div.attrs(props => ({
  "data-active": props.active ? "true" : "false",
}))`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${props => (props.active ? 1 : 0)};
  pointer-events: ${props => (props.active ? 'auto' : 'none')};
  transition: opacity 0.5s ease-in-out;
`;

const TranscriptWrapperContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
  height: 100%;
  padding: 10px;
  max-height: 100%;
  overflow-y: auto;
  scroll-behavior: smooth;
  padding-bottom: 163px;
  @media (max-width: 768px) {
    position: relative;
    z-index: 999;
    background: none;
    padding-bottom: 0;
  }
`;

const TranscriptWrapper = styled.div`
  display: flex;
  padding: 30px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 50px 20px 20px 0px;
  background: #fff;
  color: #101828;
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0.1px;
  max-height: 100%;
  overflow-y: auto;
  margin: 30px;

  @media (max-width: 768px) {
    background: none;
    color: #fff;
    text-align: center;
    font-family: Inter;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0.1px;
    width: 100%;
    padding: 12px;
    border-radius: 0;
    margin: 0;
  }
`;

const MicWrapper = styled.div`
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  gap: 12px;
  pointer-events: auto;
  z-index: 999;

  @media (max-width: 768px) {
    position: relative;
    bottom: unset;
    margin-bottom: 40px;
  }
`;

const WavesWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  pointer-events: none;
  z-index: 10;
`;

/* ─── Component ────────────────────────────────────────────────────────────── */
export default function EngageChatVoice(props) {
  const { contentSpeechAudio: initialContentSpeechAudio, contentShowFirst } = props || {};

  const [audioFinished, setAudioFinished] = useState(false);
  const [audioStarted, setAudioStarted] = useState(false);
  const [statusGenerateAudio, setStatus] = useState(null);
  const [contentSpeechAudio, setContent] = useState(null);
  const [labelVideo, setLabelVideo] = useState(null);

  const transcriptRef = useRef(null);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Scroll transcript khi content thay đổi
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [contentSpeechAudio, contentShowFirst]);

  // Cập nhật content khi status "playing" và initialContentSpeechAudio thay đổi
  useEffect(() => {
    if (statusGenerateAudio === "playing" && initialContentSpeechAudio) {
      setContent(initialContentSpeechAudio);
    }
  }, [statusGenerateAudio, initialContentSpeechAudio]);

  // Cập nhật label video theo data
  const handleUpdateEngageConversation = (data) => {
    const avatar_action = data?.avatar_action;
    const newLabelVideo = avatar_action ? avatar_action["Gesture Label"] : null;
    setLabelVideo(newLabelVideo);
  };

  // Map video theo label
  const videoMap = {
    Listening: { webm: ListeningWebm, mov: ListeningMov },
    Greeting: { webm: GreetingWebm, mov: GreetingMov },
    Instructing: { webm: InstructingWebm, mov: InstructingMov },
    Explaining: { webm: ExplainingWebm, mov: ExplainingMov },
    Acknowledging: { webm: AcknowledgingWebm, mov: AcknowledgingMov },
  };

  // Active video dựa trên status và labelVideo, mặc định Listening
  const activeLabel =
    statusGenerateAudio === "playing" && labelVideo && videoMap[labelVideo]
      ? labelVideo
      : "Listening";

  // Lấy source video tương ứng trình duyệt (Safari dùng mov)
  const getVideoSource = (label) => (isSafari ? videoMap[label].mov : videoMap[label].webm);

  const handleAudioStart = () => setAudioStarted(true);
  const handleAudioEnd = () => {
    setAudioFinished(true);
    setAudioStarted(false);
  };
  const handleStatusChange = (newStatus) => setStatus(newStatus);

  return (
    <FrameChatFullBox data-type="engage-voice">
      <ChatWrapper>
        <VideoWrapper>
          {/* Preload tất cả video, chỉ hiển thị video active */}
          {Object.keys(videoMap).map((label) => (
            <VideoLayer
              key={label}
              active={activeLabel === label}
              data-active={activeLabel === label ? "true" : "false"}
              label={label}
            >
              <ReactPlayer
                url={getVideoSource(label)}
                playing={true} // luôn chạy để preload data
                controls={false}
                loop
                muted
                playsInline
                preload="auto"
                disablePictureInPicture={true}
                width="100%"
                height="100%"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  pointerEvents: "none",
                  userSelect: "none",
                }}
                config={{
                  file: {
                    attributes: {
                      controlsList: "nofullscreen",
                      preload: "auto",
                      style: {
                        objectFit: "cover",
                        width: "100%",
                        height: "100%",
                      },
                    },
                  },
                }}
              />
            </VideoLayer>
          ))}
        </VideoWrapper>

        {(contentSpeechAudio || contentShowFirst) && (
          <TranscriptWrapperContainer>
            <TranscriptWrapper ref={transcriptRef}>
              {contentSpeechAudio || contentShowFirst}
            </TranscriptWrapper>
          </TranscriptWrapperContainer>
        )}
      </ChatWrapper>

      <MicWrapper>
        <WhisperSTTComponent
          {...props}
          audioFinished={audioFinished}
          audioStarted={audioStarted}
          statusGenerateAudio={statusGenerateAudio}
          onStatusChange={handleStatusChange}
          handleUpdateEngageConversation={handleUpdateEngageConversation}
          contentShowFirst={contentShowFirst}
        />
        <TextToSpeech
          {...props}
          onAudioStart={handleAudioStart}
          onAudioEnd={handleAudioEnd}
          onStatusChange={handleStatusChange}
        />
      </MicWrapper>

      <WavesWrapper>
        <Waves />
      </WavesWrapper>
    </FrameChatFullBox>
  );
}